/*
 * NOTE: DO NOT USE THESE FUNCTIONS DIRECTLY FROM THIS NAMESPACE.
 * 		 You should reference them from the RbUtils Namespace.
 * 		 E.g., ToroUtils.Translate.instant
 * 		 See: _toro.utils.ts
 */

import * as moment from 'moment';
import { DateTime } from 'luxon';

export namespace XXUseToroUtilsNamespace {

    export abstract class DateUtils {

        static toTimeZoneAbbreviation(date: Date): string {
            const longTimeZone = date.toString().match(/\(([A-Za-z\s].*)\)/)[1];
            const words = longTimeZone.split(' ');

            let abbr = '';
            words.forEach(w => abbr += w.slice(0, 1));

            return abbr;
        }

        /**
         * Determines whether the provided courseLocalDateString (formatted as 'yyyy-mm-dd')
         * represents the same local calendar day as the current UTC time adjusted by the given timezone offset.
         *
         * This allows for consistent "is today" checks when the database stores dates in local form,
         * and the system needs to compare against the current time in that same local timezone.
         *
         * @param courseLocalDateString - Date string in 'yyyy-mm-dd' format representing the local date of the course.
         * @param offsetSeconds - The timezone offset in seconds to adjust UTC now to local time.
         * @returns true if the adjusted local "now" matches the provided date, false otherwise.
         */
        static isDateTodayLocally(courseLocalDateString: string, offsetSeconds: number): boolean {
            // Convert the course local date string to a Luxon DateTime object
            const courseLocalDate = DateTime.fromISO(courseLocalDateString); // interpreted as local, but we will only use the date portion

            // Get "now" in local time using the same offset
            const localNow = DateTime.utc().plus({ seconds: offsetSeconds });

            // Compare year, month, and day
            return (
                courseLocalDate.year === localNow.year &&
                courseLocalDate.month === localNow.month &&
                courseLocalDate.day === localNow.day
            );
        }

        /**
         * Determines if the provided UTC date falls on "today" in local time, using the specified UTC offset.
         *
         * @param utcDate The date in UTC to evaluate.
         * @param offsetSeconds The offset from UTC in seconds (can be negative for time zones west of UTC).
         * @returns True if the utcDate falls on today's date in local time with the provided offset, false otherwise.
         */
        static isUtcDateTodayLocally(utcDate: Date, offsetSeconds: number): boolean {
            // Convert the UTC date to local time using the offset
            const localDate = DateTime.fromJSDate(utcDate, { zone: 'utc' }).plus({ seconds: offsetSeconds });

            // Get "now" in local time using the same offset
            const localNow = DateTime.utc().plus({ seconds: offsetSeconds });

            // Compare year, month, and day
            return (
                localDate.year === localNow.year &&
                localDate.month === localNow.month &&
                localDate.day === localNow.day
            );
        }

        /**
         * Determines if the provided UTC date is at least one local calendar day before "now",
         * using the specified UTC offset, ignoring the time component.
         *
         * @param utcDate The UTC date to evaluate.
         * @param offsetSeconds The offset from UTC in seconds (can be negative for time zones west of UTC).
         * @returns True if the utcDate occurred at least one calendar day ago locally, false otherwise.
         */
        static isUtcDateAtLeastOneDayAgoLocally(utcDate: Date, offsetSeconds: number): boolean {
            // Convert the UTC date to local date using the offset
            const localDate = DateTime.fromJSDate(utcDate, { zone: 'utc' })
                .plus({ seconds: offsetSeconds })
                .startOf('day');

            // Get "today" in local time using the same offset
            const localToday = DateTime.utc()
                .plus({ seconds: offsetSeconds })
                .startOf('day');

            // If localDate < localToday, it happened at least one local calendar day ago
            return localDate < localToday;
        }

        static getDeltaDays(value: string | Date, format: string = 'YYYY-MM-DD'): number {
            const target = typeof value === 'string'
                ? moment(value, format, true)
                : moment({
                    year: value.getFullYear(),
                    month: value.getMonth(),
                    day: value.getDate()
                });

            if (!target.isValid()) {
                throw new Error('Invalid date value');
            }

            // Normalize "today" to a calendar date (no time)
            const today = moment().startOf('day');

            return target.startOf('day').diff(today, 'days');
        }

    }
}
